import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiUrl } from "../lib/api";
import { useSapWarningToast } from "../lib/use-sap-warning-toast";
import { useSapSyncToast, type SapSyncResult } from "../lib/use-sap-sync-toast";

const KEY = "orders";

export function useOrderList(options?: {
	companyId?: string | undefined;
	opportunityId?: string | undefined;
	assignedTo?: string | undefined;
	status?: string | undefined;
	docNo?: string | undefined;
	product?: string | undefined;
	orderDateFrom?: string | undefined;
	orderDateTo?: string | undefined;
	country?: string | undefined;
	page?: number;
	limit?: number;
}) {
	const page = options?.page ?? 1;
	const limit = options?.limit ?? 20;
	const offset = (page - 1) * limit;
	return useQuery({
		queryKey: [KEY, "list", options],
		queryFn: () => {
			const params = new URLSearchParams();
			params.set("limit", String(limit));
			params.set("offset", String(offset));
			if (options?.companyId) params.set("companyId", options.companyId);
			if (options?.opportunityId) params.set("opportunityId", options.opportunityId);
			if (options?.assignedTo) params.set("assignedTo", options.assignedTo);
			if (options?.status) params.set("status", options.status);
			if (options?.docNo) params.set("docNo", options.docNo);
			if (options?.product) params.set("product", options.product);
			if (options?.orderDateFrom) params.set("orderDateFrom", options.orderDateFrom);
			if (options?.orderDateTo) params.set("orderDateTo", options.orderDateTo);
			if (options?.country) params.set("country", options.country);
			return api.get<{ items: Order[]; total: number }>(`/api/orders?${params.toString()}`);
		},
	});
}

export function useOrderCountries(options?: {
	status?: string | undefined;
	docNo?: string | undefined;
	companyId?: string | undefined;
	product?: string | undefined;
	assignedTo?: string | undefined;
	orderDateFrom?: string | undefined;
	orderDateTo?: string | undefined;
}) {
	return useQuery({
		queryKey: [KEY, "countries", options],
		queryFn: () => {
			const params = new URLSearchParams();
			if (options?.status) params.set("status", options.status);
			if (options?.docNo) params.set("docNo", options.docNo);
			if (options?.companyId) params.set("companyId", options.companyId);
			if (options?.product) params.set("product", options.product);
			if (options?.assignedTo) params.set("assignedTo", options.assignedTo);
			if (options?.orderDateFrom) params.set("orderDateFrom", options.orderDateFrom);
			if (options?.orderDateTo) params.set("orderDateTo", options.orderDateTo);
			return api.get<{ items: string[] }>(`/api/orders/countries?${params.toString()}`);
		},
		staleTime: 5 * 60 * 1000,
	});
}

export function useOrderCompanies() {
	return useQuery({
		queryKey: [KEY, "companies"],
		queryFn: () =>
			api.get<{ items: { id: string; name: string; currency: string | null }[] }>(
				"/api/orders/companies",
			),
		staleTime: 5 * 60 * 1000,
	});
}

export interface OrderLink {
	id: string;
	documentNumber: string | null;
	status: Order["status"];
}

export function useOrderDetail(id: string) {
	return useQuery({
		queryKey: [KEY, "detail", id],
		queryFn: () =>
			api.get<Order & { lines: OrderLine[]; copiedFrom: OrderLink | null; copies: OrderLink[] }>(
				`/api/orders/${id}`,
			),
		enabled: !!id,
	});
}

export function useCreateOrder() {
	const qc = useQueryClient();
	const warnOnSapGap = useSapWarningToast();
	return useMutation({
		mutationFn: (data: CreateOrderInput) => api.post<Order>("/api/orders", data),
		onSuccess: (order) => {
			warnOnSapGap(order);
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
		},
	});
}

export function useUpdateOrder() {
	const qc = useQueryClient();
	const warnOnSapGap = useSapWarningToast();
	const reportSapSync = useSapSyncToast();
	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<CreateOrderInput> }) =>
			api.patch<Order & SapSyncResult>(`/api/orders/${id}`, data),
		onSuccess: (order, vars) => {
			warnOnSapGap(order);
			reportSapSync(order);
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", vars.id] });
		},
	});
}

export function useDeleteOrder() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.delete<void>(`/api/orders/${id}`),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export function useCancelOrder() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.post<Order>(`/api/orders/${id}/cancel`, {}),
		onSuccess: (_, id) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", id] });
		},
	});
}

export function useCloseOrder() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.post<Order>(`/api/orders/${id}/close`, {}),
		onSuccess: (_, id) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", id] });
		},
	});
}

export function useDuplicateOrder() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => api.post<Order>(`/api/orders/${id}/duplicate`, {}),
		onSuccess: (_, id) => {
			qc.invalidateQueries({ queryKey: [KEY, "list"] });
			qc.invalidateQueries({ queryKey: [KEY, "detail", id] });
		},
	});
}

export function useRetryOrderSync() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (id: string) =>
			api.post<{ ok: boolean; message: string }>(`/api/orders/${id}/retry-sync`, {}),
		onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, "list"] }),
	});
}

export interface ProductionUnitSummary {
	serialNo: string;
	status: string;
}

export interface ProductionLineUnits {
	lineNumber: number;
	units: ProductionUnitSummary[];
}

export function useProductionUnits(orderId: string) {
	return useQuery({
		queryKey: [KEY, "production-units", orderId],
		queryFn: () =>
			api.get<{ available: boolean; lines: ProductionLineUnits[] }>(
				`/api/orders/${orderId}/production-units`,
			),
		enabled: !!orderId,
	});
}

export interface ProductionStageLog {
	action: string;
	userName: string | null;
	createdAt: string;
	note: string | null;
	reason: string | null;
}

export interface ProductionStage {
	stageCode: string;
	stageName: string;
	sequenceNo: number;
	status: "done" | "in_progress" | "waiting" | "paused" | "planned";
	startedAt: string | null;
	finishedAt: string | null;
	logs: ProductionStageLog[];
}

export interface ProductionUnitDetail {
	serialNo: string;
	itemCode: string;
	itemName: string;
	stages: ProductionStage[];
}

export function useProductionUnitDetail(orderId: string, serialNo: string | null) {
	return useQuery({
		queryKey: [KEY, "production-unit-detail", orderId, serialNo],
		queryFn: () =>
			api.get<ProductionUnitDetail>(
				`/api/orders/${orderId}/production-units/${encodeURIComponent(serialNo ?? "")}`,
			),
		enabled: !!orderId && !!serialNo,
	});
}

export interface RelationshipMapProductionOrder {
	lineNumber: number;
	itemCode: string;
	itemName: string;
	totalUnits: number;
	doneUnits: number;
	status: "done" | "in_progress" | "planned";
}

export interface RelationshipMapDocument {
	docEntry: number;
	docNum: number;
	docDate: string;
	cardCode: string;
	cardName: string;
	numAtCard: string | null;
	docTotal: number;
	docCur: string | null;
	docStatus: string;
}

export interface RelationshipMapResponse {
	order: {
		id: string;
		documentNumber: string | null;
		sapDocEntry: number | null;
		sapDocNum: number | null;
		orderDate: string | null;
		status: string;
		cardCode: string | null;
		cardName: string | null;
	};
	productionOrders: RelationshipMapProductionOrder[];
	deliveries: RelationshipMapDocument[];
	invoices: RelationshipMapDocument[];
}

export function useRelationshipMap(orderId: string | null) {
	return useQuery({
		queryKey: [KEY, "relationship-map", orderId],
		queryFn: () => api.get<RelationshipMapResponse>(`/api/orders/${orderId}/relationship-map`),
		enabled: !!orderId,
	});
}

export type SalesDocKind = "delivery" | "invoice";

export interface SalesDocLine {
	lineNum: number;
	itemCode: string;
	itemName: string;
	quantity: number;
	unitMsr: string | null;
	price: number;
	currency: string | null;
	discountPercent: number;
	vatGroup: string | null;
	lineTotal: number;
	warehouseCode: string | null;
}

export interface SalesDocDetail {
	kind: SalesDocKind;
	docEntry: number;
	docNum: number;
	docDate: string;
	docDueDate: string | null;
	taxDate: string | null;
	cardCode: string;
	cardName: string;
	numAtCard: string | null;
	docCur: string | null;
	docStatus: string;
	canceled: boolean;
	salesPerson: string | null;
	shipTo: string | null;
	comments: string | null;
	totalBeforeDiscount: number;
	discountPercent: number;
	discountTotal: number;
	vatTotal: number;
	withholdingTotal: number;
	docTotal: number;
	lines: SalesDocLine[];
}

const SALES_DOC_PATH: Record<SalesDocKind, string> = {
	delivery: "deliveries",
	invoice: "invoices",
};

export function useSalesDocDetail(
	orderId: string | null,
	kind: SalesDocKind,
	docEntry: number | null,
	url?: string,
) {
	return useQuery({
		queryKey: [KEY, "sales-doc", orderId, kind, docEntry, url],
		queryFn: () =>
			api.get<SalesDocDetail>(
				url ?? `/api/orders/${orderId}/${SALES_DOC_PATH[kind]}/${docEntry}`,
			),
		enabled: !!orderId && docEntry != null,
	});
}

export interface ChangeLogEntry {
	instance: number;
	docNum: number;
	updatedAt: string | null;
	updatedByCode: string | null;
	updatedByName: string | null;
	createdAt: string | null;
	createdByCode: string | null;
	createdByName: string | null;
	crmUpdatedBy: string[] | null;
	crmCreatedBy: string | null;
	changes: ChangeLogChange[];
}

export interface ChangeLogChange {
	field: string;
	line: number | null;
	before: string | null;
	after: string | null;
}

export function useOrderChangeLog(orderId: string | null) {
	return useQuery({
		queryKey: [KEY, "change-log", orderId],
		queryFn: () => api.get<{ entries: ChangeLogEntry[] }>(`/api/orders/${orderId}/change-log`),
		enabled: !!orderId,
		staleTime: 0,
	});
}

export type OrderAttachment =
	| { source: "sap"; line: number; name: string }
	| { source: "pending"; index: number; name: string };

export function useOrderAttachments(orderId: string | null) {
	return useQuery({
		queryKey: [KEY, "attachments", orderId],
		queryFn: () => api.get<{ items: OrderAttachment[] }>(`/api/orders/${orderId}/attachments`),
		enabled: !!orderId,
	});
}

export function orderAttachmentDownloadUrl(
	orderId: string,
	item: OrderAttachment,
	options?: { inline?: boolean },
): string {
	const params = new URLSearchParams(
		item.source === "sap"
			? { line: String(item.line) }
			: { source: "pending", index: String(item.index) },
	);
	if (options?.inline) params.set("disposition", "inline");
	return apiUrl(`/api/orders/${orderId}/attachments/download?${params.toString()}`);
}

export function allOrderAttachmentsDownloadUrl(orderId: string): string {
	return apiUrl(`/api/orders/${orderId}/attachments/download?source=all`);
}

export function useUploadOrderAttachments() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ orderId, files }: { orderId: string; files: File[] }) => {
			const formData = new FormData();
			for (const file of files) formData.append("files", file);
			return api.upload<{ staged: number }>(`/api/orders/${orderId}/attachments`, formData);
		},
		onSuccess: (_result, vars) => {
			void qc.invalidateQueries({ queryKey: [KEY, "attachments", vars.orderId] });
			void qc.invalidateQueries({ queryKey: [KEY, "list"] });
		},
	});
}

export function useDeleteOrderAttachment() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ orderId, item }: { orderId: string; item: OrderAttachment }) => {
			const params =
				item.source === "sap" ? `line=${item.line}` : `source=pending&index=${item.index}`;
			return api.delete<{ ok: boolean }>(`/api/orders/${orderId}/attachments?${params}`);
		},
		onSuccess: (_result, vars) => {
			void qc.invalidateQueries({ queryKey: [KEY, "attachments", vars.orderId] });
			void qc.invalidateQueries({ queryKey: [KEY, "list"] });
		},
	});
}

export interface Order {
	id: string;
	documentNumber: string | null;
	hasAttachments: boolean;
	orderDate: string | null;
	deliveryDate: string | null;
	currency: string | null;
	exchangeRate: string | null;
	paymentTerms: string | null;
	deliveryTerms: string | null;
	shippingAddress: string | null;
	shippingAddressId: string | null;
	notes: string | null;
	internalNotes: string | null;
	startupDescription: string | null;
	installationDescription: string | null;
	tmsDescription: string | null;
	atsDescription: string | null;
	factorySpecialNotes: string | null;
	generalNotes: string | null;
	subtotal: string | null;
	vatTotal: string | null;
	total: string | null;
	paidAmount: string | null;
	paymentStatus: "unpaid" | "partially_paid" | "paid" | "overpaid";
	isCustomDesign: boolean;
	status:
		| "draft"
		| "confirmed"
		| "partially_delivered"
		| "delivered"
		| "invoiced"
		| "closed"
		| "cancelled";
	companyId: string | null;
	contactPersonId: string | null;
	opportunityId: string | null;
	sourceOfferId: string | null;
	copiedFromOrderId: string | null;
	assignedTo: string | null;
	teamId: string | null;
	visibility: string;
	companyName: string | null;
	companySapCardCode?: string | null;
	companySapAccountId?: string | null;
	country?: string | null;
	createdByName?: string | null;
	createdByEmail?: string | null;
	assignedToName?: string | null;
	assignedToEmail?: string | null;
	assignedToSapName?: string | null;
	sapDocEntry: number | null;
	sapDocNum: number | null;
	syncStatus: "pending" | "in_progress" | "synced" | "failed" | "manual_review" | null;
	syncError: string | null;
	companySyncError?: string | null;
	sapWarning?: string | null;
	productionSummary: { totalUnits: number; doneUnits: number } | null;
	createdAt: string;
	updatedAt: string;
}

interface OrderLine {
	id: string;
	lineNumber: number;
	itemCode: string | null;
	itemDescription: string | null;
	displayDescription: string | null;
	quantity: string;
	unit: string | null;
	unitPrice: string;
	vatRate: string | null;
	vatGroup: string | null;
	discountPercent: string | null;
	lineTotal: string | null;
	notes: string | null;
	atsDescription: string | null;
	tmsDescription: string | null;
	factorySpecialNotes: string | null;
	generalNotes: string | null;
	isCustomDesign: boolean;
	stockItemId: string | null;
	warehouseId: string | null;
	lineDetail: string | null;
	priceListModel: string | null;
	priceComponents: { label: string; price: number; separate?: boolean }[] | null;
	warehouseName: string | null;
	warehouseCode: string | null;
}

interface CreateOrderInput {
	orderDate?: string | undefined;
	deliveryDate?: string | undefined;
	currency?: string | undefined;
	exchangeRate?: number | undefined;
	paymentTerms?: string | undefined;
	deliveryTerms?: string | undefined;
	shippingAddress?: string | undefined;
	shippingAddressId?: string | undefined;
	notes?: string | undefined;
	internalNotes?: string | undefined;
	startupDescription?: string | undefined;
	installationDescription?: string | undefined;
	companyId?: string | undefined;
	contactPersonId?: string | undefined;
	opportunityId?: string | undefined;
	sourceOfferId?: string | undefined;
	assignedTo?: string | undefined;
	subtotal?: number | undefined;
	vatTotal?: number | undefined;
	total?: number | undefined;
	paidAmount?: number | undefined;
	paymentStatus?: "unpaid" | "partially_paid" | "paid" | "overpaid" | undefined;
	status?: string | undefined;
	lines: {
		lineNumber: number;
		itemCode?: string | undefined;
		itemDescription?: string | undefined;
		displayDescription?: string | undefined;
		quantity: number;
		unit?: string | undefined;
		unitPrice: number;
		vatRate?: number | undefined;
		vatGroup?: string | undefined;
		discountPercent?: number | undefined;
		lineTotal?: number | undefined;
		notes?: string | undefined;
		lineDetail?: string | undefined;
		atsDescription?: string | undefined;
		tmsDescription?: string | undefined;
		factorySpecialNotes?: string | undefined;
		generalNotes?: string | undefined;
		priceListModel?: string | undefined;
		priceComponents?: { label: string; price: number; separate?: boolean }[] | undefined;
		isCustomDesign?: boolean | undefined;
		stockItemId?: string | undefined;
		warehouseId?: string | undefined;
	}[];
}
